import {
  DATE_PRESETS,
  FLIPKART_BRAND_ID,
  GA_ACTION,
  GA_CATEGORIES,
  IMAGE_FILE_EXTENSIONS,
  IMAGE_PREFIX,
  IMAGE_PREFIX_AZURE,
  IMAGE_PREFIX_SOURCING_ASSETS,
  LINK_REGEX,
  MEESHO_PILOT_3_BRAND_ID,
  NON_PRODUCT_LINK_IDENTIFIERS,
  PAGE_URLS,
  POST_TYPES,
  PURE_ORIGIN_BRAND_ID,
  ZERO_COMMISSION,
  SOCIAL_LOGIN_STORAGE_KEYS,
  URL_REGEX,
} from "./constants";
import { transformMedia } from "./mediaTransform";
import { getAnalytics, setUserId, setUserProperties } from "firebase/analytics";
import {
  getAppBuildVersion,
  getAppSessionUuid,
  getPlatform,
} from "../utils/config";
import { StyledImage } from "@wish-ui";

import { auth } from "./firebase";
import { resetAuth } from "../stores/auth.store";
import { loadState, saveState } from "./localStorage";
import { triggerGa } from "./gaUtils";

export const addHours = (date, hours) => {
  const dateCopy = new Date(date);
  dateCopy.setTime(dateCopy.getTime() + hours * 60 * 60 * 1000);
  return dateCopy;
};

export const refreshFirebaseAuthStatusOrSignOut = async () => {
  const currentUser = auth.currentUser;
  if (currentUser) {
    const token = await currentUser.getIdToken();
    saveState("tokenExpiry", addHours(new Date(), 1));
    saveState("firebaseToken", token);
  } else {
    await signOutFirebaseAuth();
  }
};

export const signOutFirebaseAuth = async () => {
  const creatorSourceFromLocalStorage = loadState("creatorSource");
  const utmSourceFromLocalStorage = loadState("utmSource");
  const features = loadState("features");
  localStorage.clear();
  if (creatorSourceFromLocalStorage) {
    saveState("creatorSource", creatorSourceFromLocalStorage);
  }
  if (utmSourceFromLocalStorage) {
    saveState("utmSource", utmSourceFromLocalStorage);
  }
  if (features) {
    saveState("features", features);
  }
  // Firebase's signOut does not notify listeners when there is no user, so an
  // admin session would otherwise keep a stale isLoggedIn until a full reload.
  resetAuth();
  await auth.signOut();
};

export const getResponsiveWidth = (widthFactor = 100) => {
  return window.innerWidth > 448
    ? `${(28 * widthFactor) / 100}rem`
    : window.innerWidth < 360
    ? `${(22.5 * widthFactor) / 100}rem`
    : `${(window.innerWidth * widthFactor) / 100 / 16}rem`;
};

export const copyToClipboard = async (textToCopy) => {
  await window.customCopy(textToCopy, navigator);
};

const openAndroidShare = ({ textToShare }) => {
  window?.ReactNativeWebView?.postMessage(
    JSON.stringify({ type: "share", payload: textToShare })
  );
};

export const openShareDrawer = async (textToShare) => {
  openAndroidShare({ textToShare });
  try {
    await window.customShare(textToShare, navigator);
  } catch (e) {
    // console.log("error opening share drawer", e);
  }
};

export const copyToClipboardAndOpenShareDrawer = async (textToCopyAndShare) => {
  openAndroidShare({ textToShare: textToCopyAndShare });
  try {
    await window.customCopy(textToCopyAndShare, navigator);
    await window.customShare(textToCopyAndShare, navigator);
  } catch (e) {
    // console.log("error opening share drawer", e);
  }
};

/**
 * @param {string} linkToOpen
 * @param {{ utmSource?: string, utmMedium?: string }} [options]
 */
export const openLinkInNewTab = (linkToOpen, { utmSource, utmMedium } = {}) => {
  openExternalLinkInNewTab({ redirectUrl: linkToOpen, utmSource, utmMedium });
};

export const formatShortDate = (date) => {
  if (!date) return "";
  const dateObj = new Date(date);
  if (isNaN(dateObj)) return "";
  const day = dateObj.getDate();
  const month = dateObj.toLocaleString("en-US", { month: "short" });
  const year = dateObj.getFullYear();

  return `${day} ${month}, ${year}`;
};
export const getFormattedDate = (date) => {
  return new Date(date).toLocaleDateString("en-us", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const getFormattedDateWithoutYear = (date) => {
  return new Date(date).toLocaleDateString("en-us", {
    month: "short",
    day: "numeric",
  });
};

const allCommissionAreSameNonZero = (products) => {
  if (!products?.length) return false;
  let commission = products[0]?.commission;
  for (let i = 1; i < products.length; i++) {
    if (products[i]?.commission !== commission) {
      return false;
    }
  }

  return true;
};

const allCommissionsAreZero = (products) => {
  for (let i = 0; i < products.length; i++) {
    if (products[i]?.commission !== ZERO_COMMISSION) {
      return false;
    }
  }
  return true;
};

export const getCommissionRangeForAnalytics = (products) => {
  if (!products || products?.length === 0) {
    return ZERO_COMMISSION;
  }
  if (allCommissionsAreZero(products)) {
    return "Non commissionable";
  } else if (allCommissionAreSameNonZero(products)) {
    if (
      products.some(
        (product) =>
          product?.commission_new !== product?.commission_old ||
          product?.category_wise_commissions?.length ||
          product?.commission_additional_info_text?.length
      )
    ) {
      return `Upto ${products[0]?.commission}`;
    } else {
      return `Flat ${products[0]?.commission}`;
    }
  } else {
    let minCommission = 100;
    let maxCommission = 0;
    for (let i = 0; i < products.length; i++) {
      let productCommission = products[i]?.commission
        ? parseFloat(products[i]?.commission)
        : 0;
      if (productCommission > maxCommission) {
        maxCommission = productCommission;
      }
      if (productCommission < minCommission) {
        minCommission = productCommission;
      }
    }
    if (minCommission === maxCommission) {
      return `${minCommission}%`;
    } else {
      return `Upto ${maxCommission}%`;
    }
  }
};

export const getBrandCategoryWiseMaxCommission = (
  commissions,
  live_commission
) => {
  if (
    commissions === undefined ||
    commissions?.creator_specific_commission_exists === true
  )
    return "";
  if (commissions.length === 0) {
    return live_commission;
  }
  const range = commissions.reduce(
    (acc, commission) => {
      if (commission.creator_commission_pct < acc.min) {
        acc.min = commission.creator_commission_pct;
      }
      if (commission.creator_commission_pct > acc.max) {
        acc.max = commission.creator_commission_pct;
      }
      return acc;
    },
    { min: Infinity, max: -Infinity }
  );
  return ` ${range.min}% - ${range.max}%`;
};

export const getDataPointLoadingValue = (dataPoint) => {
  return dataPoint?.status === "pending";
};

export const getAssetUrl = (assetUrl) => {
  const url = IMAGE_PREFIX + assetUrl;
  return transformMedia(url);
};

export const getAssetUrlAzure = (assetUrl) => {
  const url = IMAGE_PREFIX_AZURE + assetUrl;
  return transformMedia(url);
};

export const getSourcingAssetUrl = (assetUrl) => {
  const url = IMAGE_PREFIX_SOURCING_ASSETS + assetUrl;
  return transformMedia(url);
};

export const getImageUrlFromCategoryNamePng = (categoryName) => {
  if (categoryName === "") return "";
  const url = IMAGE_PREFIX_SOURCING_ASSETS + categoryName + ".png";
  return transformMedia(url);
};

export const getImageUrlFromCategoryNameJpeg = (categoryName) => {
  const url = IMAGE_PREFIX_SOURCING_ASSETS + categoryName + ".jpeg";
  return transformMedia(url);
};

export const getOpacityForDisabledValue = (disabled) => {
  return disabled ? "0.4" : "1";
};

export function getCroppedImg(image, crop) {
  let scale = 0;
  let y_diff = 0;
  let x_diff = 0;
  const canvas = document.createElement("canvas");
  if (image.naturalWidth > image.naturalHeight) {
    scale = image.naturalWidth / image.width;
    y_diff = (image.height - image.naturalHeight / scale) / 2;
  } else {
    scale = image.naturalHeight / image.height;
    x_diff = (image.width - image.naturalWidth / scale) / 2;
  }
  canvas.width = crop.width * 4;
  canvas.height = crop.height * 4;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(
    image,
    (crop.x - x_diff) * scale,
    (crop.y - y_diff) * scale,
    crop.width * scale,
    crop.height * scale,
    0,
    0,
    crop.width * 4,
    crop.height * 4
  );
  // As a blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        blob.name = "croppedImage";
        resolve(blob);
      },
      "image/jpeg",
      1
    );
  });
}
export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const getDisplayDateRange = (startDate, endDate) => {
  return `${startDate?.getDate()} ${
    MONTHS[startDate?.getMonth()]
  } - ${endDate?.getDate()} ${MONTHS[endDate?.getMonth()]}`;
};

export const getStartAndEndDateFromPreset = (preset) => {
  let today = new Date();
  let startDate = new Date();
  let endDate = new Date();
  if (preset === DATE_PRESETS.LAST_MONTH) {
    startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    endDate = new Date(today.getFullYear(), today.getMonth(), 0);
  } else if (preset === DATE_PRESETS.YESTERDAY) {
    endDate = new Date(today.setDate(today.getDate() - 1));
    startDate = endDate;
  } else if (preset === DATE_PRESETS.TODAY) {
    startDate = today;
  } else if (preset === DATE_PRESETS.ALL_TIME) {
    startDate = new Date(2020, 1, 1, 0, 0, 0, 0);
  } else if (preset === DATE_PRESETS.THIS_MONTH) {
    startDate = new Date(today.getFullYear(), today.getMonth(), 1);
  } else if (preset === DATE_PRESETS.LAST_2_MONTHS) {
    startDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    endDate = new Date(today.getFullYear(), today.getMonth(), 0);
  } else if (preset === DATE_PRESETS.LAST_3_MONTHS) {
    startDate = new Date(today.getFullYear(), today.getMonth() - 3, 1);
    endDate = new Date(today.getFullYear(), today.getMonth(), 0);
  } else if (preset === DATE_PRESETS.LAST_6_MONTHS) {
    startDate = new Date(today.getFullYear(), today.getMonth() - 6, 1);
    endDate = new Date(today.getFullYear(), today.getMonth(), 0);
  } else if (preset === DATE_PRESETS.LAST_7_DAYS) {
    startDate = new Date(today.setDate(today.getDate() - 6));
  } else if (preset === DATE_PRESETS.LAST_TO_LAST_MONTH) {
    startDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    endDate = new Date(today.getFullYear(), today.getMonth() - 1, 0);
  }
  return [startDate, endDate];
};

export const formatDate = (date) => {
  let year = date.toLocaleString("en-US", { year: "numeric" });
  let month = date.toLocaleString("en-US", { month: "2-digit" });
  let day = date.toLocaleString("en-US", { day: "2-digit" });
  return year + "-" + month + "-" + day;
};

export function getDateObjectFromDateString(date) {
  let dateArray = date?.split("-");
  let dateObject = null;
  if (dateArray?.length === 3)
    dateObject = new Date(
      parseInt(dateArray[0]),
      parseInt(dateArray[1]) - 1,
      parseInt(dateArray[2])
    );
  return dateObject;
}

export const getDisplayTextFromDatesAndPresets = (
  startDate,
  endDate,
  preset,
  allowedPresets
) => {
  if (preset && allowedPresets?.includes(preset)) {
    return preset;
  } else {
    let startDateToDate = new Date(startDate);
    let endDateToDate = new Date(endDate);
    return getDisplayDateRange(startDateToDate, endDateToDate);
  }
};

export const getShareAllLinksText = (
  creatorUsername,
  postId,
  type,
  products,
  postLink = ""
) => {
  let text = "";
  if (type === "collection") {
    text = `*Collection Link:* ${getPostURL(
      creatorUsername,
      postId,
      type
    )}\n\n*Short link for stories:* \n\n${getAllProductShortUrls(products)}`;
  } else {
    if (postLink?.includes("instagram.com")) {
      text = `*Post Link:* ${getPostURL(
        creatorUsername,
        postId,
        type
      )}\n\n*Short link for stories:* \n\n${getAllProductShortUrls(products)}`;
    } else {
      text = `*All Product Links:* ${getPostURL(
        creatorUsername,
        postId,
        type
      )}\n\n*Individual Product Links:* \n\n${getAllProductShortUrls(
        products
      )}`;
    }
  }
  return text;
};

export const getAllProductShortUrls = (products) => {
  return products
    .filter((prod) => prod.is_alive)
    .map(
      (product, i) =>
        `${i + 1}. ${product?.name}: ${
          product?.shorturl || product?.short_url
        }\n`
    )
    .join("\n");
};

export const getPostURL = (creatorUsername, postId, kind) => {
  return `https://wishlink.com/${creatorUsername}/${kind}/${postId}`;
};

export const formatAmountK = (amount) => {
  let formattedAmount = "";
  let isNegative = false;
  let magnitude = 0;
  if (typeof amount != "number") {
    return formattedAmount;
  }
  if (amount < 0) {
    isNegative = true;
  }
  amount = Math.abs(Math.ceil(amount));
  if (amount >= 1000) {
    magnitude += 1;
    amount = amount / 1000;
    while (amount >= 100 && magnitude < 3) {
      magnitude += 1;
      amount = amount / 100;
    }
  }
  amount = Number(amount.toPrecision(3));
  return (
    (isNegative ? "-" : "") +
    amount?.toString() +
    ["", "K", "L", "Cr"][magnitude]
  );
};

export const createDIYPostObj = (inputObj, type) => {
  const diyPost = {};
  if (type === "instagram" || type === "facebook") {
    diyPost.post_url = inputObj?.permalink;
    diyPost.media_type = inputObj?.media_type;
    diyPost.thumbnail_url = inputObj?.thumbnail_url || inputObj?.media_url;
    diyPost.media_url = inputObj?.media_url;
    diyPost.post_social_media_id = inputObj?.id;
    diyPost.post_added_on_social_media = inputObj?.timestamp;
    diyPost.children = inputObj?.children || {};
  } else if (type === "youtube") {
    diyPost.post_url = inputObj?.post_url;
    diyPost.media_type = "VIDEO";
    diyPost.thumbnail_url = `https://img.youtube.com/vi/${inputObj?.contentDetails?.videoId}/0.jpg`;
    diyPost.media_url = `https://www.youtube.com/embed/${inputObj?.contentDetails?.videoId}`;
    diyPost.post_social_media_id = "";
    diyPost.post_added_on_social_media = inputObj?.snippet?.publishedAt;
    diyPost.privacy_status = inputObj?.status?.privacyStatus;
  } else {
    // console.log("We shouldn't have been here :(");
  }
  return diyPost;
};

export const triggerGoBackGa = (pageName, screenName) => {
  triggerGa({
    category: GA_CATEGORIES.NAVIGATION,
    action: GA_ACTION.BACK,
    pageName: pageName,
    screenName: screenName,
  });
  return true;
};

export const getPostTypeIcon = (postType) => {
  if (!postType) return "";

  const type = postType.toLowerCase();

  if (type === "insta_reel")
    return transformMedia(`${IMAGE_PREFIX}insta_reel.png`);
  if (type === "insta_image")
    return transformMedia(`${IMAGE_PREFIX}insta_post.png`);
  if (type === "insta_story")
    return transformMedia(`${IMAGE_PREFIX}insta_story.png`);

  if (type === "fb_image") return transformMedia(`${IMAGE_PREFIX}fb_post.png`);
  if (type === "fb_video") return transformMedia(`${IMAGE_PREFIX}fb_video.png`);
  if (type === "fb_reel") return transformMedia(`${IMAGE_PREFIX}fb_reel.png`);

  if (type === "yt_shorts")
    return transformMedia(`${IMAGE_PREFIX}yt_shorts.svg`);
  if (type === "yt_video") return transformMedia(`${IMAGE_PREFIX}yt_video.svg`);

  if (type === "pin_image" || type === "pin_carousel" || type === "pin_video")
    return transformMedia(`${IMAGE_PREFIX}pinterest_pin.png`);

  if (type === "snap_spotlight")
    return transformMedia(
      "https://images.wishlink.com/alabaster/wishlink_media_1779275640.png"
    );

  return "";
};

export const getDIYGaBasedOnFirstPost = (firstPostExists) => {
  return firstPostExists ? GA_CATEGORIES.CREATE : GA_CATEGORIES.FIRST_POST;
};

export const triggerGAUserId = (creatorId) => {
  let adminToken = localStorage.adminToken;
  if (!adminToken) {
    const analytics = getAnalytics();
    setUserId(analytics, creatorId);
    setUserProperties(analytics, {
      user_id: creatorId,
      platform: getPlatform(),
      app_build: getAppBuildVersion(),
      app_open_id: getAppSessionUuid(),
    });
  }
};

export const getInstaLoginUrl = (isEngageViaInstagram) => {
  let origin = window.location.origin;
  const client_id = process.env.REACT_APP_INSTAGRAM_OAUTH_CLIENT_ID;
  if (window.ReactNativeWebView) {
    origin = origin.replace("app://", "https://");
  }
  if (isEngageViaInstagram) {
    return `https://www.instagram.com/oauth/authorize?wishlink_external=true&client_id=${client_id}&redirect_uri=${origin}/auth/code&response_type=code&scope=instagram_business_basic%2Cinstagram_business_manage_messages%2Cinstagram_business_manage_comments%2Cinstagram_business_manage_insights#weblink`;
  } else {
    return `https://www.instagram.com/oauth/authorize?wishlink_external=true&client_id=${client_id}&redirect_uri=${origin}/auth/code&response_type=code&scope=instagram_business_basic#weblink`;
  }
};

export const triggerInstagramLogin = (isEngageViaInstagram = false) => {
  const adminToken = localStorage.getItem("adminToken");
  if (adminToken) {
    return;
  }

  const redirectUrl = getInstaLoginUrl(isEngageViaInstagram);
  openExternalLinkInNewTab({
    redirectUrl,
  });
};

// Facebook Login permissions for POST /{ig-media-id}/product_tags, added to the Facebook Connect login
// for IG_PRODUCT_TAG creators only. The router sets the flag from creator config, so every
// triggerFacebookLogin() caller picks it up without changes.
export const IG_PRODUCT_TAG_LOGIN_SCOPES = [
  "instagram_basic",
  "instagram_shopping_tag_products",
];
let igProductTagLoginEnabled = false;
export const setIgProductTagLoginEnabled = (enabled) => {
  igProductTagLoginEnabled = !!enabled;
};

export const triggerFacebookLogin = () => {
  const adminToken = localStorage.getItem("adminToken");
  if (adminToken) {
    return;
  }

  let redirect_uri =
    window.location.origin.replace("http://", "https://") +
    "/facebook/callback";

  if (window.ReactNativeWebView) {
    redirect_uri = redirect_uri.replace("app://", "https://");
  }
  const client_id = "665365291834413";
  const response_type = "code";

  const scope = [
    "email",
    "read_insights",
    "public_profile",
    "pages_show_list",
    "pages_manage_metadata",
    "pages_messaging",
    "pages_read_engagement",
    "pages_show_list",
    "business_management",
    "pages_manage_posts",
    "pages_manage_engagement",
    "publish_video",
    ...(igProductTagLoginEnabled ? IG_PRODUCT_TAG_LOGIN_SCOPES : []),
  ];

  const redirectUrl = `https://www.facebook.com/v24.0/dialog/oauth?client_id=${client_id}&redirect_uri=${redirect_uri}&response_type=${response_type}&scope=${scope}#weblink`;
  openExternalLinkInNewTab({ redirectUrl });
};

export const triggerPinterestLogin = () => {
  const client_id = process.env.REACT_APP_PINTEREST_OAUTH_CLIENT_ID;
  openExternalLinkInNewTab({
    redirectUrl: `https://www.pinterest.com/oauth/?response_type=code&redirect_uri=${window.location.origin.replace(
      "app://",
      "https://"
    )}/pinterest-callback&client_id=${client_id}&scope=boards:read,boards:write,pins:read,pins:write,user_accounts:read`,
  });
};

export const getSnapchatRedirectUri = () =>
  `${window.location.origin.replace("app://", "https://")}${
    PAGE_URLS.SNAPCHATLOGINSUCCESS
  }`;

export const triggerSnapchatLogin = () => {
  const client_id = process.env.REACT_APP_SNAPCHAT_OAUTH_CLIENT_ID;
  const redirect_uri = getSnapchatRedirectUri();
  openExternalLinkInNewTab({
    redirectUrl: `https://accounts.snapchat.com/login/oauth2/authorize?response_type=code&client_id=${client_id}&redirect_uri=${encodeURIComponent(
      redirect_uri
    )}&scope=snapchat-profile-api&state=snapchat_crosspost`,
  });
};

export const loadGoogleScript = (callback) => {
  if (document.getElementById("google-youtube-signin")) {
    if (callback) callback();
    return;
  }
  const googleScript = document.createElement("script");
  googleScript.src = "https://accounts.google.com/gsi/client";
  googleScript.defer = true;
  googleScript.id = "google-youtube-signin";
  if (callback) {
    googleScript.onload = () => callback();
  }
  document.head.appendChild(googleScript);
};

export const triggerYoutubeLogin = async () => {
  const adminToken = localStorage.getItem("adminToken");
  if (adminToken) {
    return;
  }

  const autoLoginApp = localStorage.getItem("loginApp");
  if (autoLoginApp && autoLoginApp === "yt") {
    await signOutFirebaseAuth();
  }
  /* global google */
  const client = google.accounts.oauth2.initCodeClient({
    client_id:
      process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID ||
      window.Cypress.env("google_oauth_client_id"),
    // eslint-disable-next-line no-useless-escape
    scope: `https://www.googleapis.com/auth/youtube.readonly \ https://www.googleapis.com/auth/youtubepartner \ https://www.googleapis.com/auth/yt-analytics.readonly`,
    ux_mode: "redirect",
    redirect_uri: `${window.location.origin}/youtube-auth`,
    access_type: "offline",
    callback: () => {},
  });
  client.requestCode();
};

export const triggerEngageLogin = (
  redirect_uri = window.location.origin + "/auth/success"
) => {
  const client_id = process.env.REACT_APP_FACEBOOK_OAUTH_CLIENT_ID;
  const response_type = "token";

  const scope = [
    "email",
    "public_profile",
    "pages_show_list",
    "instagram_basic",
    "instagram_manage_insights",
    "instagram_manage_comments",
    "instagram_manage_messages",
    "pages_manage_metadata",
    "pages_messaging",
    "pages_read_engagement",
    "pages_show_list",
    "business_management",
    "pages_manage_posts",
    "publish_video",
    "pages_manage_engagement",
  ];

  const redirectUrl = `https://www.facebook.com/v24.0/dialog/oauth?client_id=${client_id}&redirect_uri=${redirect_uri}&response_type=${response_type}&scope=${scope}#weblink`;
  openExternalLinkInNewTab({ redirectUrl });
};

export const preloadImage = (url) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = url;
    image.onload = () => resolve(url); // Resolve with URL to identify the image that loaded
    image.onerror = (error) =>
      reject(new Error(`Failed to load image at ${url}: ${error.message}`));
  });
};

export const preloadImages = (urls) => {
  const promises = urls.map((url) => preloadImage(url));
  return Promise.all(promises)
    .then((results) => {
      return results;
    })
    .catch((error) => {
      //console.error("Error loading images:", error);
    });
};

export const checkIfLinkIsValidScrapingLink = (
  link,
  scrapingFailureDomains = [],
  username = ""
) => {
  let returnObject = { success: true, message: "" };
  if (username === "wishlinkconnect" || username === "trialroom") {
    return returnObject;
  }
  if (!link) {
    returnObject.message = "Please add a link";
  }
  if (typeof link !== "string") {
    returnObject.message = "Please add a link";
  }
  link = link?.toLowerCase();
  let isUrlType = LINK_REGEX.test(link);
  if (!isUrlType) {
    returnObject.message =
      "Please enter a product link, this does not seem to be a link.";
  }
  if (
    NON_PRODUCT_LINK_IDENTIFIERS.some((str) => link.includes(str.toLowerCase()))
  ) {
    returnObject.message =
      "Please add a product link, this seems like a social media link";
  }
  if (
    link.includes("wishlink.com/") &&
    !(
      link.includes("wishlink.com/share/") ||
      link.includes("wishlink.com/buy/") ||
      link.includes("creator.wishlink.com/brand-sourcing/product/")
    ) &&
    username !== "wishlinksrishti" &&
    username !== "wishlinkconnect"
  ) {
    returnObject.message =
      "Please add a product link, this seems like a wishlink storefront url";
  }
  try {
    let processedUrl = new URL(link);
    let path = processedUrl.pathname;
    for (const str of IMAGE_FILE_EXTENSIONS) {
      if (path.endsWith(str)) {
        returnObject.message =
          "Please add a product link, this seems like an image url";
        break;
      }
    }
  } catch (e) {
    returnObject.message = "Please add a valid product link";
  }
  for (const domain of scrapingFailureDomains) {
    if (link?.includes(domain)) {
      returnObject.message = "Please add a valid product link";
      break;
    }
  }
  if (returnObject.message !== "") {
    returnObject.success = false;
  }
  return returnObject;
};

export const checkInvalidLinkExists = (links, scrapingFailureDomains = []) => {
  let invalidLinkExists = false;
  for (const link of links) {
    let validity = checkIfLinkIsValidScrapingLink(link, scrapingFailureDomains);
    if (!validity.success && link !== "") {
      invalidLinkExists = true;
      break;
    }
  }
  return invalidLinkExists;
};

export const checkValidLinkExists = (links, scrapingFailureDomains = []) => {
  let validLinkExists = false;
  for (const link of links) {
    let validity = checkIfLinkIsValidScrapingLink(link, scrapingFailureDomains);
    if (validity.success) {
      validLinkExists = true;
      break;
    }
  }
  return validLinkExists;
};

export const getTimeAgoTextFromDateAndCurrentTimestamps = (
  dateSent,
  dateToday
) => {
  let dateDelta = dateToday - dateSent;
  let daysDelta = Math.floor(dateDelta / (1000 * 60 * 60 * 24));

  if (daysDelta < 0) {
    return "now";
  } else if (daysDelta === 0) {
    let secondsDelta = Math.floor(dateDelta / 1000);

    if (secondsDelta < 60) {
      if (secondsDelta === 0) {
        return "now";
      }
      return `${secondsDelta}s`;
    } else if (secondsDelta >= 60 && secondsDelta < 3600) {
      let minutesDelta = Math.floor(secondsDelta / 60);
      return `${minutesDelta}m`;
    } else {
      let hoursDelta = Math.floor(secondsDelta / 3600);
      return `${hoursDelta}h`;
    }
  } else if (daysDelta >= 1 && daysDelta < 7) {
    return `${daysDelta}d`;
  } else if (daysDelta >= 7 && daysDelta < 30) {
    let weeksDelta = Math.floor(daysDelta / 7);
    return `${weeksDelta}w`;
  } else if (daysDelta >= 30 && daysDelta < 360) {
    let monthsDelta = Math.floor(daysDelta / 30);
    return `${monthsDelta}M`;
  } else {
    if (daysDelta < 365) {
      return "1y";
    } else {
      let yearsDelta = Math.floor(daysDelta / 365);
      return `${yearsDelta}y`;
    }
  }
};

export const getkFormatting = (int) => {
  if (typeof int === "string") {
    int = parseInt(int);
  }
  if (int >= 100000) {
    int = (int / 100000).toFixed(2);
    int = int + "L";
    return int;
  }
  if (int >= 1000) {
    int = (int / 1000).toFixed(2);
    int = int + "K";
    return int;
  }
  return int.toFixed(2);
};
export const getkFormattingWithoutDecimal = (
  int,
  hideZeroAfterDecimal = false
) => {
  if (typeof int === "string") {
    int = parseInt(int);
  }

  //removed toFixed as it rounded off the number
  if (int >= 10000000) {
    int = Math.floor(int / 100000) / 100;
    int = int + "Cr";
    return int;
  }

  if (int >= 100000) {
    int = Math.floor(int / 1000) / 100;
    int = int + "L";
    return int;
  }

  if (int >= 1000) {
    int = Math.floor(int / 100) / 10;
    int = int + "K";
    return int;
  }

  if (int < 10) {
    if (hideZeroAfterDecimal) return int;
    return int.toFixed(1);
  }

  return Math.floor(int);
};

/** Reel view/click metric label; "" (hidden) when zero/missing. */
export const formatReelMetric = (value) => {
  if (value == null || value === "") return "";
  const n = typeof value === "number" ? value : parseInt(String(value), 10);
  if (!Number.isFinite(n) || n <= 0) return "";
  return String(getkFormattingWithoutDecimal(n, true));
};

export const formatViews = (views) => {
  if (!views || views === "NA") return "NA";

  const num = parseInt(views);
  if (isNaN(num)) return "NA";

  if (num < 1000) {
    return num.toString();
  } else if (num >= 1000 && num < 1000000) {
    const kValue = (num / 1000).toFixed(1);
    return kValue.endsWith(".0") ? `${kValue.slice(0, -2)}k` : `${kValue}k`;
  } else if (num >= 1000000) {
    const mValue = (num / 1000000).toFixed(1);
    return mValue.endsWith(".0") ? `${mValue.slice(0, -2)}M` : `${mValue}M`;
  }

  return num.toString();
};

const maxCommssionFromCategory = (category, liveCommission) => {
  if (!category || category?.creator_specific_commission_exists === true)
    return "";
  if (category.length === 0 && liveCommission) {
    return liveCommission;
  }
  let maxCommission = 0;
  category?.forEach((brand) => {
    if (brand.creator_commission_pct > maxCommission) {
      maxCommission = brand.creator_commission_pct;
    }
  });
  return maxCommission;
};

export const getMaxCategoryWiseBrandCommission = (category, liveCommission) => {
  if (!category || category?.creator_specific_commission_exists === true)
    return "";
  const maxCommission = maxCommssionFromCategory(category, liveCommission);
  return `upto ${maxCommission}%`;
};

export const redirectUserToExternalLink = (external_link) => {
  openExternalLinkInNewTab({ redirectUrl: external_link });
};

export const appendUtmParams = (url, { utmSource, utmMedium } = {}) => {
  if (!url || (!utmSource && !utmMedium)) return url;
  try {
    const params = [];
    if (utmSource) params.push(`utm_source=${encodeURIComponent(utmSource)}`);
    if (utmMedium) params.push(`utm_medium=${encodeURIComponent(utmMedium)}`);
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${params.join("&")}`;
  } catch (e) {
    return url;
  }
};

/**
 * @param {{ redirectUrl: string, utmSource?: string, utmMedium?: string }} params
 */
export const openExternalLinkInNewTab = ({
  redirectUrl,
  utmSource,
  utmMedium,
}) => {
  redirectUrl = appendUtmParams(redirectUrl, { utmSource, utmMedium });
  const build = getAppBuildVersion();
  if (window.ReactNativeWebView) {
    if (build <= 24) {
      redirectUrl = redirectUrl.includes("?")
        ? redirectUrl + "&wishlink_external=true"
        : redirectUrl + "?wishlink_external=true";
      window.open(redirectUrl, "_blank", "noreferrer");
    } else {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "open_external_link",
          payload: { redirectUrl },
        })
      );
    }
  } else {
    window.open(redirectUrl, "_blank", "noreferrer");
  }
};

const UPTO_BRAND_IDS = [PURE_ORIGIN_BRAND_ID, FLIPKART_BRAND_ID];

export const getUptoOrFlatCommissionText = (BrandId) => {
  if (UPTO_BRAND_IDS.includes(BrandId)) {
    return "Upto";
  }
  return "Flat";
};

export async function convertBlobUrlToBase64(blobObj) {
  const blobUrlString = URL.createObjectURL(blobObj);
  const response = await fetch(blobUrlString, {
    headers: {
      Accept: "image/*", // Ensure the server knows you expect an image
    },
  });
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64data = reader.result;
      resolve(base64data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export const shareBlobImage = async ({ message, blob }) => {
  const platform = getPlatform();
  const build = getAppBuildVersion();
  const imageData = await convertBlobUrlToBase64(blob);
  if (platform === "android" && build >= 23) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "share_base64_image",
        payload: {
          options: { message: message },
          imageUrl: imageData,
        },
      })
    );
    return true;
  }
  return false;
};

export const openAppSettings = (reason = "microphone") => {
  window.ReactNativeWebView?.postMessage?.(
    JSON.stringify({
      type: "open_app_settings",
      payload: { reason },
    })
  );
};

export function openPopupInAppBrowser(url) {
  if (window.ReactNativeWebView && getAppBuildVersion() >= 52) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "popup_in_app_browser",
        payload: { url },
      })
    );
  } else {
    openExternalLinkInNewTab({ redirectUrl: url });
  }
}

export const hasCommissionDetails = (listItem) => {
  return (
    listItem?.commission !== "0%" ||
    listItem?.commission_new !== "0%" ||
    listItem?.commission_old !== "0%" ||
    listItem?.category_wise_commissions?.length > 0 ||
    listItem?.commission_additional_info_text?.length > 0
  );
};

export const isFlatOrUptoCommission = (brand, isDrawer = false) => {
  const isFlat = Boolean(
    brand?.min_commission === brand?.max_commission &&
      !brand?.commission_additional_info_text?.length &&
      !brand?.category_wise_commissions?.length &&
      brand?.min_commission !== "0%"
  );

  if (isDrawer) {
    return isFlat;
  } else {
    // Meesho rate is the direct-order commission; indirect orders pay less, so show "Upto" (0% stays flat)
    if (brand?.brand_id === MEESHO_PILOT_3_BRAND_ID)
      return !(Number(brand?.max_commission) > 0);
    else return isFlat;
  }
};

export const isZeroCommission = (brand) => {
  return brand.newCommission === "0%" && brand.oldCommission === "0%";
};

export const getPostTypeLabel = (post_url) => {
  if (!post_url) return "";

  const lowerUrl = post_url?.toLowerCase();

  if (lowerUrl.includes("youtube.com") || lowerUrl.includes("youtu.be")) {
    return "postyt";
  } else if (lowerUrl.includes("instagram.com")) {
    return "postig";
  } else if (lowerUrl.includes("facebook.com")) {
    return "postfb";
  } else {
    return undefined;
  }
};

export const isFeatureNew = () => {
  const today = new Date();
  const start = new Date("2025-09-08");
  const oneMonthInMs = 30 * 24 * 60 * 60 * 1000;
  return today - start < oneMonthInMs;
};

export const getPostType = (post, color = "#000") => {
  const postType = post?.post_type?.type?.toLowerCase();

  if (!postType) return null;
  if (postType === "insta_reel") {
    return (
      <StyledImage
        width="18px"
        height="18px"
        src={transformMedia(`${IMAGE_PREFIX}insta_reel.png`)}
        alt="instagram reel"
      />
    );
  } else if (postType === "insta_image") {
    return (
      <StyledImage
        width="18px"
        height="18px"
        src={transformMedia(`${IMAGE_PREFIX}insta_post.png`)}
        alt="instagram post"
      />
    );
  } else if (postType === "insta_story") {
    return (
      <StyledImage
        width="18px"
        height="18px"
        src={transformMedia(`${IMAGE_PREFIX}insta_story.png`)}
        alt="instagram story"
      />
    );
  }

  // Facebook content types
  else if (postType === "fb_post") {
    return (
      <StyledImage
        width="18px"
        height="18px"
        src={transformMedia(`${IMAGE_PREFIX}fb_post.png`)}
        alt="facebook post"
      />
    );
  } else if (postType === "fb_video") {
    return (
      <StyledImage
        width="18px"
        height="18px"
        src={transformMedia(`${IMAGE_PREFIX}fb_video.png`)}
        alt="facebook video"
      />
    );
  } else if (postType === "fb_reel") {
    return (
      <StyledImage
        width="18px"
        height="18px"
        src={transformMedia(`${IMAGE_PREFIX}fb_reel.png`)}
        alt="facebook reel"
      />
    );
  }

  // YouTube content types
  else if (postType === "yt_shorts") {
    return (
      <StyledImage
        width="18px"
        height="18px"
        src={transformMedia(`${IMAGE_PREFIX}yt_shorts.svg`)}
        alt="youtube shorts"
      />
    );
  } else if (postType === "yt_video") {
    return (
      <StyledImage
        width="18px"
        height="18px"
        src={transformMedia(`${IMAGE_PREFIX}yt_video.svg`)}
        alt="youtube video"
      />
    );
  }

  // Pinterest content types
  else if (
    postType === "pin_image" ||
    postType === "pin_carousel" ||
    postType === "pin_video"
  ) {
    return (
      <StyledImage
        width="18px"
        height="18px"
        src={transformMedia(`${IMAGE_PREFIX}pinterest_pin.png`)}
        alt="pinterest pin"
      />
    );
  } else if (postType === "snap_spotlight") {
    return (
      <StyledImage
        width="24px"
        height="24px"
        src={transformMedia(
          "https://images.wishlink.com/alabaster/wishlink_media_1779275640.png"
        )}
        alt="snapchat spotlight"
      />
    );
  }

  // Fallback: use icon_url from API if available
  if (post?.post_type?.icon_url) {
    return (
      <StyledImage
        width="18px"
        height="18px"
        src={post.post_type.icon_url}
        alt={postType}
      />
    );
  }

  return null;
};
export function isElementCompletelyVisible(className) {
  const isVisible = false;

  const element = document.querySelector("." + className);

  if (element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= window.innerHeight &&
      rect.right <= window.innerWidth
    );
  }
  return isVisible;
}

export function isElementPresentInDom(className) {
  const element = document.querySelector("." + className);
  return !!element;
}

export function isElementCompletelyVisibleAboveFooter(
  className,
  footerHeight = getPlatform() === "web" ? 76 : 64
) {
  const isVisible = false;

  const element = document.querySelector("." + className);

  if (element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= window.innerHeight - footerHeight &&
      rect.right <= window.innerWidth
    );
  }
  return isVisible;
}

export const getGaClientId = () => {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    if (
      trimmed.startsWith("_ga=GA1.1.") &&
      document.location.hostname.includes("wishlink")
    ) {
      const parts = trimmed.split(".");
      if (parts.length >= 4) {
        return `${parts[2]}.${parts[3]}`;
      }
    }
  }
  return null;
};

export const getPlatformFromStorage = () => {
  let creatorSource = {};
  try {
    creatorSource = JSON.parse(localStorage.getItem("creatorSource"));
  } catch (error) {
    // do nothing
  }
  let platform = "";
  if (creatorSource && Object.keys(creatorSource).length > 0)
    platform = creatorSource.device_platform;
  else platform = getPlatform();
  return platform;
};

export const formatTimestampToTime = (timestamp) => {
  try {
    const date =
      timestamp instanceof Date
        ? timestamp
        : typeof timestamp === "number"
        ? new Date(timestamp)
        : new Date(timestamp);
    if (isNaN(date.getTime())) return "";
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "pm" : "am";
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, "0");
    return `${displayHours}:${displayMinutes}${ampm}`; // Formatted time string
  } catch (error) {
    return "";
  }
};

export const formatAmountToIndianFormat = (amount, options = {}) => {
  const {
    includeDecimals = true,
    useShortFormat = false,
    currency = "₹",
  } = options;

  if (amount === null || amount === undefined || amount === "") {
    return includeDecimals ? `${currency} 0.00` : `${currency} 0`;
  }

  let numericAmount =
    typeof amount === "string"
      ? parseFloat(amount.replace(/[₹,]/g, ""))
      : amount;

  if (isNaN(numericAmount)) {
    return includeDecimals ? `${currency} 0.00` : `${currency} 0`;
  }

  const isNegative = numericAmount < 0;
  const absoluteAmount = Math.abs(numericAmount);

  if (useShortFormat) {
    if (absoluteAmount >= 10000000) {
      const crores = (absoluteAmount / 10000000).toFixed(2);
      return `${isNegative ? "-" : ""}${currency} ${crores}Cr`;
    } else if (absoluteAmount >= 100000) {
      const lakhs = (absoluteAmount / 100000).toFixed(2);
      return `${isNegative ? "-" : ""}${currency} ${lakhs}L`;
    } else if (absoluteAmount >= 1000) {
      const thousands = (absoluteAmount / 1000).toFixed(2);
      return `${isNegative ? "-" : ""}${currency} ${thousands}K`;
    }
  }

  const roundedAmount = Math.round(absoluteAmount * 100) / 100;
  const [integerPart, decimalPart = "00"] = roundedAmount.toFixed(2).split(".");
  const formattedInteger = parseInt(integerPart, 10).toLocaleString("en-IN");
  const formattedAmount = includeDecimals
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;

  return `${isNegative ? "-" : ""}${currency} ${formattedAmount}`;
};

export const clearExpiredDismissalFlags = (authorizations) => {
  if (!authorizations) return;

  // Clear Instagram dismissal if not expired
  if (authorizations.is_expired_insta?.achieved === false) {
    localStorage.removeItem(
      SOCIAL_LOGIN_STORAGE_KEYS.EXPIRED_DISMISSED.instagram
    );
  }

  // Clear YouTube dismissal if not expired
  if (authorizations.is_expired_yt?.achieved === false) {
    localStorage.removeItem(
      SOCIAL_LOGIN_STORAGE_KEYS.EXPIRED_DISMISSED.youtube
    );
  }

  // Clear Facebook dismissal if not expired
  if (authorizations.is_expired_facebook?.achieved === false) {
    localStorage.removeItem(
      SOCIAL_LOGIN_STORAGE_KEYS.EXPIRED_DISMISSED.facebook
    );
  }

  // Clear "not connected accessed" flags when creator is no longer waitlisted
  // AND has successfully connected the platform
  const isWaitlisted = authorizations.is_creator_waitlisted?.achieved;

  // Only clear Instagram flag if user is connected AND not waitlisted
  if (
    isWaitlisted === false &&
    authorizations.is_connected_insta?.achieved === true
  ) {
    localStorage.removeItem(
      SOCIAL_LOGIN_STORAGE_KEYS.NOT_CONNECTED_ACCESSED_DIY.instagram
    );
  }

  // Only clear YouTube flag if user is connected AND not waitlisted
  if (
    isWaitlisted === false &&
    authorizations.is_connected_yt?.achieved === true
  ) {
    localStorage.removeItem(
      SOCIAL_LOGIN_STORAGE_KEYS.NOT_CONNECTED_ACCESSED_DIY.youtube
    );
  }

  // Only clear Facebook flag if user is connected AND not waitlisted
  if (
    isWaitlisted === false &&
    authorizations.is_connected_facebook?.achieved === true
  ) {
    localStorage.removeItem(
      SOCIAL_LOGIN_STORAGE_KEYS.NOT_CONNECTED_ACCESSED_DIY.facebook
    );
  }
};

const pickUrlLine = (text) => {
  for (const line of text.split(/[\n\r]+/)) {
    const trimmedLine = line.trim();
    if (trimmedLine.match(/https?:\/\/|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}/)) {
      return trimmedLine;
    }
  }
  return "";
};

export const cleanLinkFromEncodedText = (url) => {
  if (!url || typeof url !== "string") {
    return "";
  }
  // Work on the raw input first: URLs can legitimately contain encoded control
  // chars (e.g. Myntra puts %0A in share-URL slugs when the product title has
  // a newline) — decoding those first would truncate the URL at the line-split.
  let urlLine = pickUrlLine(url);
  if (!urlLine.includes("://")) {
    // No plain URL in the raw input — it may be percent-encoded text (possibly
    // holding several URLs joined by encoded newlines). Decode and re-pick,
    // but only trust the decoded result when it reveals a scheme URL (or the
    // raw pass found nothing): a scheme-less link like "www.x.com/a%0Ab/1"
    // must stay undecoded or the %0A would truncate it.
    try {
      const decodedLine = pickUrlLine(decodeURIComponent(url));
      if (decodedLine && (decodedLine.includes("://") || !urlLine)) {
        urlLine = decodedLine;
      }
    } catch (e) {
      // malformed encoding — keep whatever the raw pass found
    }
  }
  return urlLine || url.trim();
};

// Pull every anchor href out of an HTML clipboard payload and append any URL
// that is not already present in the plain-text payload. Uses DOMParser +
// getAttribute so URLs containing quotes/apostrophes (e.g. Myntra ".../men's-...")
// are captured intact — a regex over href="..." truncates at the first apostrophe.
export const extractUrlsFromHtml = (htmlData, plainText = "") => {
  if (!htmlData) return null;
  let hrefs = [];
  try {
    const doc = new DOMParser().parseFromString(htmlData, "text/html");
    hrefs = Array.from(doc.querySelectorAll("a[href]"), (a) =>
      a.getAttribute("href")
    );
  } catch (e) {
    hrefs = [];
  }
  hrefs = hrefs.filter((url) => !!url && /https?:\/\//i.test(url));
  if (hrefs.length === 0) return null;
  const hiddenUrls = hrefs.filter((url) => !plainText?.includes(url));
  if (hiddenUrls.length === 0) return null;
  return `${plainText || ""}\n${hiddenUrls.join("\n")}`.trim();
};

export const extractUrlsFromClipboardData = (clipboardData) => {
  const htmlData = clipboardData?.getData("text/html");
  const plainText = clipboardData?.getData("text/plain") || "";
  return extractUrlsFromHtml(htmlData, plainText) || plainText;
};

// Best-effort recovery of the real URL on platforms where the paste event's
// clipboardData is stripped (notably the RN WebView). Reads the async Clipboard
// API for the HTML / uri-list representations that hold the href even when the
// plain-text representation is just the product title. Resolves to null when the
// API is unavailable or blocked (caller then falls back to window.customPaste).
export const readRichClipboard = async () => {
  try {
    if (!navigator?.clipboard?.read) return null;
    const items = await navigator.clipboard.read();
    let html = "";
    let uriList = "";
    let text = "";
    for (const item of items) {
      if (!html && item.types.includes("text/html")) {
        html = await (await item.getType("text/html")).text();
      }
      if (!uriList && item.types.includes("text/uri-list")) {
        uriList = await (await item.getType("text/uri-list")).text();
      }
      if (!text && item.types.includes("text/plain")) {
        text = await (await item.getType("text/plain")).text();
      }
    }
    const fromHtml = extractUrlsFromHtml(html, text);
    // uri-list lines starting with "#" are comments per RFC 2483.
    const uriListUrls = uriList
      .split(/[\r\n]+/)
      .map((line) => line.trim())
      .filter((line) => line && !line.startsWith("#"))
      .filter((line) => !text.includes(line));
    if (fromHtml) return fromHtml;
    if (uriListUrls.length)
      return `${text || ""}\n${uriListUrls.join("\n")}`.trim();
    return text || null;
  } catch (e) {
    return null;
  }
};

// Put concatenated http(s):// links on separate lines so links pasted
// back-to-back with no separator are detected as distinct URLs. URL_REGEX's
// tail is [^\s]* — without this a second link with no whitespace before it gets
// swallowed into the first link's path. Creators no longer need to manually add
// spaces/newlines. Only splits when the scheme is preceded by a URL character:
// a scheme after "=" is an embedded redirect/tracking target (e.g.
// ".../redirect?url=https://product.com") and a scheme after whitespace is
// already separated — neither must be broken apart. No lookbehind (unsupported
// on older RN WebView / iOS Safari) — a capture group keeps the boundary char.
export const splitConcatenatedUrls = (text) => {
  if (!text || typeof text !== "string") return text || "";
  return text.replace(/([^\s=])(https?:\/\/)/gi, "$1\n$2").trim();
};

// Strip everything except the URLs from pasted text. Brand "share" clipboards
// carry a product title line + the link; creators only want the link in the box.
// Returns each detected URL on its own line. Falls back to the trimmed input
// when no URL is found so a non-link paste is not silently discarded.
export const keepOnlyUrls = (text) => {
  if (!text || typeof text !== "string") return "";
  // URL_REGEX matches from the domain (its char class excludes "//"), so match
  // with an optional scheme prefix to retain the leading https:// in the box.
  const urlWithScheme = new RegExp("(https?:\\/\\/)?" + URL_REGEX.source, "gi");
  const matches = splitConcatenatedUrls(text).match(urlWithScheme);
  return matches && matches.length ? matches.join("\n") : text.trim();
};

export const getMilestoneKey = (milestones, prefix) =>
  Object.keys(milestones).find((key) => key.startsWith(prefix));

export const withLibasUtmSource = (url) => {
  if (!url) return url;
  const [baseWithoutHash, hashPart] = url.split("#");
  const [pathPart, queryPart = ""] = baseWithoutHash.split("?");
  const params = new URLSearchParams(queryPart);
  params.set("utm_source", "libas_sale_experiment");
  const query = params.toString();
  return `${pathPart}${query ? `?${query}` : ""}${
    hashPart ? `#${hashPart}` : ""
  }`;
};

export const isCustomEngagePdpAlive = (product) => {
  if (!product?.custom_engage_values) return !!product?.is_alive;
  return !!product.custom_engage_values.is_alive;
};

// Native signs the OG data at fetch time, and the signed bytes are the only thing the backend
// reads when the signature verifies (og_fetch.py:verify_client_og_attestation) — so the flat
// copy customFetchOgData returns alongside them is pure duplication on the wire. Old app builds
// send no attestation and keep the flat copy, which is all the backend can use for them.
//
// Trimmed at the network boundary rather than in customFetchOgData because the flat fields are
// also what the sourcing forms read for client-side prefill; those never hit the backend.
export const trimClientOgData = (client_og_data) =>
  client_og_data?.attestation
    ? { attestation: client_og_data.attestation }
    : client_og_data;
